import * as React from "react";
import { FormGroup } from "../RightAsideHelpers";
import Select from "@/components/Select";
import { httpMethods, WorkflowNode } from "../../types";
import Input from "@/components/Input";
import { IconClose } from "@velaro/velaro-component-lib";
import { TertiaryGrayButton } from "@velaro/velaro-component-lib";
import { IconPlus } from "@velaro/velaro-component-lib";
import TextArea from "@/components/TextArea";
import { useMemo, useRef } from "react";
import { Toggle } from "@velaro/velaro-component-lib";
import VariablePicker from "@/components/VariablePicker";
import VariableHelpCard from "@/components/VariableHelpCard";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function HttpEditor({ node, onUpdate }: Props) {
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const bodyIsValidJson = useMemo(() => {
    try {
      if (!node.body) {
        return true;
      }
      JSON.parse(node.body);
      return true;
    } catch (e) {
      return false;
    }
  }, [node.body]);

  function insertIntoBody(variable: string) {
    const el = bodyRef.current;
    if (el) {
      const start = el.selectionStart ?? (node.body?.length || 0);
      const end = el.selectionEnd ?? start;
      const current = node.body || "";
      const newText =
        current.substring(0, start) + variable + current.substring(end);
      onUpdate({ body: newText });
      requestAnimationFrame(() => {
        el.focus();
        const newPos = start + variable.length;
        el.setSelectionRange(newPos, newPos);
      });
    } else {
      onUpdate({ body: (node.body || "") + variable });
    }
  }

  return (
    <>
      <VariableHelpCard context="httpRequest" />
      <FormGroup label="Description">
        <Input
          value={node.description || ""}
          onChange={(description) => onUpdate({ description })}
        />
      </FormGroup>
      <FormGroup label="Method">
        <Select
          options={httpMethods}
          value={node.httpMethod}
          onChange={(httpMethod) => {
            onUpdate({ httpMethod });
          }}
        />
      </FormGroup>
      <FormGroup label="Url">
        <Input
          value={node.url || ""}
          onChange={(url) => onUpdate({ url })}
          suffixIcon={
            <VariablePicker
              onInsert={(v) => onUpdate({ url: (node.url || "") + v })}
            />
          }
        />
      </FormGroup>
      <FormGroup label="Headers">
        <div className="border rounded-lg p-2 mb-4 bg-slate-gray-100 flex flex-col items-center">
          {node.headers &&
            node.headers.map((header, i) => (
              <React.Fragment key={i}>
                <div className="flex w-full items-center gap-1">
                  <Input
                    placeholder="Key"
                    className="my-2 flex grow"
                    value={header.key}
                    onChange={(key) => {
                      node.headers[i] = { ...header, key };
                      onUpdate({
                        headers: [...node.headers]
                      });
                    }}
                  />
                  <IconClose
                    onClick={() => {
                      onUpdate({
                        headers: node.headers.filter((_, index) => index !== i)
                      });
                    }}
                    className="cursor-pointer"
                    style={{ color: "#CBD5E1" }}
                  />
                </div>
                <Input
                  placeholder="Value"
                  className="my-2"
                  value={header.value}
                  onChange={(value) => {
                    node.headers[i] = { ...header, value };
                    onUpdate({
                      headers: [...node.headers]
                    });
                  }}
                  suffixIcon={
                    <VariablePicker
                      onInsert={(v) => {
                        node.headers[i] = {
                          ...header,
                          value: header.value + v
                        };
                        onUpdate({ headers: [...node.headers] });
                      }}
                    />
                  }
                />
                <div className="my-2 border border-slate-300 w-full"></div>
              </React.Fragment>
            ))}
          <TertiaryGrayButton
            prefixIcon={<IconPlus />}
            label="Add Header"
            onClick={() => {
              onUpdate({
                headers: [...(node.headers || []), { key: "", value: "" }]
              });
            }}
          />
        </div>
      </FormGroup>
      <FormGroup label="Body">
        <div className="relative">
          <div className="absolute right-1 top-1 z-10">
            <VariablePicker onInsert={insertIntoBody} />
          </div>
          <TextArea
            ref={bodyRef}
            className={bodyIsValidJson ? "" : "border border-red-300"}
            value={node.body || ""}
            placeholder="JSON"
            onChange={(e) => onUpdate({ body: e.currentTarget.value })}
          />
        </div>
      </FormGroup>
      <FormGroup label="Settings">
        <Toggle
          toggled={node.waitForResponse || false}
          changeToggle={() =>
            onUpdate({ waitForResponse: !node.waitForResponse })
          }
          label="Wait for response"
        />
      </FormGroup>
    </>
  );
}
